import { useContext, useEffect, useState } from "react";
import {
  Container,
  Card,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Box,
  Tooltip,
  IconButton,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { AppCtx } from "../../store/store";
import { HighlightOff } from "@mui/icons-material";
import {
  getTenancyContract,
  submitTenancyEndNotice,
} from "../../apis/property/tenancy";
import { TenanacyContract } from "../../models/property/tenancy";
import { showSnackBar } from "../../functions/snackbar";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

export const SubmitNotice = () => {
  const navigate = useNavigate();
  const { setIsLoading, getLoggedUser, isLoading } = useContext(AppCtx);
  const loggedUser = getLoggedUser();
  const { propertyId } = useParams();
  const [tenancyContract, setTenancyContract] = useState<TenanacyContract>();
  const [isDisabled, setIsDisabled] = useState(true);
  const [rentDueDate, setRentDueDate] = useState(dayjs());

  const hasAlreadySubmittedNotice = tenancyContract?.noticeEndDate != null;
  const minNoticeDate = dayjs().add(1, "month").add(1, "day").startOf("day");

  useEffect(() => {
    fetchTenancyContract();
  }, []);

  const schema = yup.object({
    noticeDate: yup
      .date()
      .min(
        minNoticeDate.toDate(),
        `Date must be after ${minNoticeDate.format("DD-MM-YYYY")}`
      )
      .max(
        rentDueDate.add(1, "month").toDate(),
        `Date must be before next rent due date: ${rentDueDate
          .add(1, "month")
          .format("YYYY-MM-DD")}`
      )
      .required("Please select a notice date"),
    reason: yup.string().optional(),
    confirm: yup.boolean().oneOf([true], "You must confirm before submitting"),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      noticeDate: dayjs(),
      reason: "",
      confirm: false,
    },
  });

  async function fetchTenancyContract() {
    setIsLoading(true);
    let tempTenancyContract = (await getTenancyContract(
      propertyId!,
      loggedUser?.token!
    )) as unknown as TenanacyContract;
    setTenancyContract(tempTenancyContract);
    reset({
      noticeDate: tempTenancyContract.noticeEndDate
        ? dayjs(tempTenancyContract.noticeEndDate)
        : undefined,
      reason: tempTenancyContract.tenancyEndReason ?? "",
      confirm: tempTenancyContract.noticeEndDate !== null,
    });
    setIsDisabled(
      tempTenancyContract.noticeEndDate !== null &&
        tempTenancyContract.tenancyEndIsAccepted !== false
    );

    const currentDate = new Date();
    const month =
      (tempTenancyContract?.rentDueDay ?? 30) < currentDate.getDate()
        ? currentDate.getMonth() + 1
        : currentDate.getMonth();

    var tempRentDueDate = dayjs()
      .set("date", tempTenancyContract?.rentDueDay ?? 30)
      .set("month", month);

    setRentDueDate(tempRentDueDate);
    setIsLoading(false);
  }

  const onSubmit = async () => {
    if (
      tenancyContract?.contractStatus === "Let Only" &&
      hasAlreadySubmittedNotice
    ) {
      alert("You have already submitted a notice for this tenancy.");
      return;
    }
    setIsLoading(true);
    await submitTenancyEndNotice(
      control._formValues["noticeDate"],
      control._formValues["reason"],
      tenancyContract?.contractId!,
      tenancyContract?.moveOutDate!,
      loggedUser?.name!,
      propertyId!,
      loggedUser?.token!
    );
    await fetchTenancyContract();
    setIsLoading(false);
    showSnackBar("success", "Tenancy End Notice Submitted!");
  };

  return (
    <Container maxWidth="sm">
      <Card
        variant="outlined"
        sx={{
          position: "relative", // allow absolute children
          borderRadius: 3,
          p: 3,
        }}
      >
        {/* Tag in top-right corner */}
        {!isLoading && tenancyContract?.tenancyEndIsAccepted !== null && (
          <Box
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              bgcolor: tenancyContract?.tenancyEndIsAccepted ? "green" : "red",
              color: "white",
              px: 2,
              py: 0.5,
              borderRadius: 2,
              fontSize: 12,
              fontWeight: "bold",
            }}
          >
            {tenancyContract?.tenancyEndIsAccepted ? "Accepted" : "Rejected"}
          </Box>
        )}
        <Box display="flex" justifyContent="center" mb={2}>
          <img
            src="/images/move_out.jpg"
            alt="Moving out"
            style={{
              width: "100%",
              maxHeight: 200,
              objectFit: "contain",
              borderRadius: 12,
            }}
          />
        </Box>
        <Typography variant="h6" textAlign={"center"} gutterBottom>
          Submit Notice to Vacate
        </Typography>
        <Typography textAlign={"center"} variant="body2" gutterBottom>
          Rent Due Date: <strong>{rentDueDate.format("DD/MM/YYYY")}</strong>
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit, (e) => {
            console.log(e);
          })}
          noValidate
        >
          <Controller
            control={control}
            name="noticeDate"
            render={({ field }) => (
              <FormControl fullWidth>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    disabled={isDisabled}
                    value={field.value ?? null}
                    onChange={(date) => field.onChange(date)}
                    label={"Notice Date"}
                    format="DD/MM/YYYY"
                  />
                </LocalizationProvider>
              </FormControl>
            )}
          />
          {errors.noticeDate && (
            <FormHelperText error>{errors.noticeDate.message}</FormHelperText>
          )}
          <Controller
            name="reason"
            control={control}
            render={({ field }) => (
              <TextField
                disabled={isDisabled}
                label="Reason (Optional)"
                multiline
                rows={3}
                fullWidth
                margin="normal"
                {...field}
              />
            )}
          />

          <Controller
            name="confirm"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                disabled={isDisabled}
                control={
                  <Checkbox
                    color="primary"
                    {...field}
                    checked={isDisabled ? isDisabled : field.value}
                  />
                }
                label="I confirm that I wish to submit my notice."
              />
            )}
          />
          {errors.confirm && (
            <Typography variant="caption" color="error">
              {errors.confirm.message}
            </Typography>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
            disabled={isDisabled}
          >
            Submit Notice
          </Button>
        </Box>
        <Box display="flex" justifyContent="center" mt={2}>
          <Tooltip title={"Back to property page"}>
            <IconButton color="primary" onClick={() => navigate("/properties")}>
              <HighlightOff sx={{ fontSize: 60 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Card>
    </Container>
  );
};
